import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { API_ORIGIN } from '../../../services/apiBase';

// CMS OASIS-E schema — abbreviated set of key elements for the form engine.
// Full schema has 100+ items; this covers the most clinically critical ones.
const OASIS_SCHEMA = [
  {
    section: "M-Series: Clinical",
    items: [
      { id: "M0010", label: "CMS Certification Number", type: "text" },
      { id: "M1033", label: "Risk for Hospitalization", type: "radio",
        options: [["0","No risks"],["1","1-2 risk factors"],["2","3+ risk factors"],["3","4+ high-risk factors"]] },
      { id: "M1306", label: "Pressure Ulcer/Injury Risk", type: "radio",
        options: [["0","No risk"],["1","Low risk"],["2","High risk"]] },
      { id: "M1308", label: "Current Number of Unhealed Pressure Ulcers", type: "number" },
      { id: "M1350", label: "Skin Lesion or Open Wound", type: "radio",
        options: [["0","No"],["1","Yes"]] },
      { id: "M1400", label: "When is the patient dyspneic?", type: "radio",
        options: [["0","Never"],["1","With moderate exertion"],["2","With minimal exertion"],["3","At rest"],["4","At rest, O2 dependent"]] },
      { id: "M1500", label: "Symptoms in heart failure last 12 months", type: "checkbox",
        options: [["1","No"],["2","Patient does not have cardiac diagnosis"],["3","Patient had no symptoms"],["4","One episode"],["5","Two or more episodes"]] },
      { id: "M1700", label: "Cognitive Functioning", type: "radio",
        options: [["0","Alert/oriented"],["1","Requires prompting"],["2","Requires assistance"],["3","Requires considerable assistance"],["4","Totally dependent"]] },
      { id: "M2200", label: "Therapy Need (visits/week)", type: "number" },
    ],
  },
  {
    section: "GG-Series: Functional Abilities",
    items: [
      { id: "GG0100A", label: "Prior Functioning: Self-Care", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate assist"],["02","Substantial/maximal assist"],["01","Dependent"],["07","N/A"]] },
      { id: "GG0100B", label: "Prior Functioning: Indoor Mobility", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate assist"],["02","Substantial/maximal assist"],["01","Dependent"],["07","N/A"]] },
      { id: "GG0100C", label: "Prior Functioning: Stairs", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate assist"],["02","Substantial/maximal assist"],["01","Dependent"],["07","N/A"]] },
      { id: "GG0130A", label: "Eating (admission)", type: "radio",
        options: [["06","Independent"],["05","Setup/cleanup"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
      { id: "GG0130B", label: "Oral Hygiene (admission)", type: "radio",
        options: [["06","Independent"],["05","Setup/cleanup"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
      { id: "GG0130C1", label: "Toileting Hygiene (admission)", type: "radio",
        options: [["06","Independent"],["05","Setup/cleanup"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
      { id: "GG0170C1", label: "Roll Left and Right (admission)", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
      { id: "GG0170D1", label: "Sit to Lying (admission)", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
      { id: "GG0170E1", label: "Lying to Sitting on Side of Bed", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"],["88","Not attempted"]] },
    ],
  },
  {
    section: "Discharge Goals",
    items: [
      { id: "GG0130A3", label: "Eating (discharge goal)", type: "radio",
        options: [["06","Independent"],["05","Setup/cleanup"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"]] },
      { id: "GG0130B3", label: "Oral Hygiene (discharge goal)", type: "radio",
        options: [["06","Independent"],["05","Setup/cleanup"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"]] },
      { id: "GG0170C3", label: "Roll Left and Right (discharge goal)", type: "radio",
        options: [["06","Independent"],["04","Supervised"],["03","Partial/moderate"],["02","Substantial/maximal"],["01","Dependent"]] },
    ],
  },
];

const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7',
  borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };

export default function OasisAssessment() {
  const { id } = useParams();  // assessment_id
  const navigate = useNavigate();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const canEdit = roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE'].includes(r));

  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError] = useState('');
  const autoSaveRef = useRef(null);

  const apiBase = `${API_ORIGIN}/api/v1`;
  const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                           'Content-Type': 'application/json' });

  useEffect(() => {
    if (!id) return;
    fetch(`${apiBase}/oasis-assessments/${id}`, { headers: headers() })
      .then(r => r.json()).then(d => {
        const a = d.data || d;
        setAssessment(a);
        let r = a.responses || {};
        if (typeof r === 'string') r = JSON.parse(r);
        setResponses(r);
      }).catch(() => setError('Failed to load assessment'));
  }, [id]);

  const save = useCallback(async (silent = false) => {
    if (!id || !canEdit) return;
    if (assessment?.status === 'LOCKED') return;
    if (!silent) setSaving(true);
    try {
      await fetch(`${apiBase}/oasis-assessments/${id}`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ responses }),
      });
      if (!silent) setSaveMsg('Saved ✓');
    } catch {
      if (!silent) setError('Save failed');
    } finally {
      if (!silent) setSaving(false);
    }
  }, [id, responses, canEdit, assessment]);

  // Auto-save every 60 seconds
  useEffect(() => {
    autoSaveRef.current = setInterval(() => save(true), 60000);
    return () => clearInterval(autoSaveRef.current);
  }, [save]);

  async function complete() {
    if (!window.confirm('Complete and lock this assessment? This cannot be undone.')) return;
    setCompleting(true); setError('');
    try {
      const r = await fetch(`${apiBase}/oasis-assessments/${id}/complete`, {
        method: 'POST', headers: headers(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      const result = d.data || d;
      setAssessment(a => ({ ...a, status: 'LOCKED', hipps_code: result.hipps_code }));
      setSaveMsg(`Completed! HIPPS code: ${result.hipps_code}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCompleting(false);
    }
  }

  const isLocked = assessment?.status === 'LOCKED';
  const completedPct = Math.round(
    (Object.keys(responses).length /
      OASIS_SCHEMA.reduce((s, sec) => s + sec.items.length, 0)) * 100
  );

  if (!assessment) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#8b7681', fontFamily: 'Inter, sans-serif' }}>
      {error || 'Loading assessment…'}
    </div>
  );

  const currentSection = OASIS_SCHEMA[activeSection];

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', fontFamily: 'Inter, sans-serif', overflow: 'hidden' }}>
      {/* Section sidebar */}
      <div style={{ width: 220, background: '#f9f5f6', borderRight: '1px solid #ece3e7',
        padding: '16px 0', overflowY: 'auto', flexShrink: 0 }}>
        <div style={{ padding: '0 16px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#5e4a53', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: 8 }}>Progress</div>
          <div style={{ height: 6, background: '#ece3e7', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#7b1e3c', width: `${completedPct}%`,
              transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 11, color: '#8b7681', marginTop: 4 }}>{completedPct}% complete</div>
        </div>
        {OASIS_SCHEMA.map((sec, i) => (
          <div key={i} onClick={() => setActiveSection(i)} style={{
            padding: '10px 16px', cursor: 'pointer', fontSize: 13,
            fontWeight: activeSection === i ? 700 : 400,
            background: activeSection === i ? '#f0e8ed' : 'transparent',
            color: activeSection === i ? '#7b1e3c' : '#5e4a53',
            borderLeft: activeSection === i ? '3px solid #7b1e3c' : '3px solid transparent',
          }}>{sec.section}</div>
        ))}
      </div>

      {/* Main form area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#2b1b22', margin: '0 0 4px' }}>
              OASIS-E: {assessment.assessment_type}
            </h1>
            <div style={{ fontSize: 12, color: '#8b7681' }}>
              Patient: {assessment.patient_name || assessment.patient_id?.slice(0, 8) || '—'}
              {assessment.hipps_code && (
                <span style={{ marginLeft: 16, background: '#7b1e3c', color: '#fff',
                  padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                  HIPPS: {assessment.hipps_code}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: isLocked ? '#fee2e2' : assessment.status === 'IN_PROGRESS' ? '#fef9c3' : '#e0e7ff',
              color: isLocked ? '#991b1b' : assessment.status === 'IN_PROGRESS' ? '#92400e' : '#3730a3',
            }}>{assessment.status}</span>
            {!isLocked && canEdit && (
              <>
                <button onClick={() => save(false)} disabled={saving} style={{
                  padding: '8px 16px', background: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13,
                  cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>{saving ? 'Saving…' : 'Save Draft'}</button>
                <button onClick={complete} disabled={completing} style={{
                  padding: '8px 16px', background: 'linear-gradient(180deg,#94335a,#7b1e3c)',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 13,
                  fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                }}>{completing ? 'Completing…' : 'Complete & Lock'}</button>
              </>
            )}
          </div>
        </div>

        {saveMsg && (
          <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 14px',
            borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{saveMsg}</div>
        )}
        {error && (
          <div style={{ background: '#fdecea', color: '#b71c1c', padding: '8px 14px',
            borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>
        )}

        <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#7b1e3c', marginTop: 0, marginBottom: 20 }}>
            {currentSection.section}
          </h2>
          {currentSection.items.map(item => (
            <OasisItem key={item.id} item={item} value={responses[item.id] || ''}
              onChange={v => {
                setResponses(r => ({ ...r, [item.id]: v }));
                setSaveMsg('');
              }}
              disabled={isLocked || !canEdit} />
          ))}
        </div>

        {/* Section navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <button disabled={activeSection === 0} onClick={() => setActiveSection(s => s - 1)} style={{
            padding: '9px 18px', background: '#f3f4f6', color: '#374151',
            border: 'none', borderRadius: 8, cursor: activeSection === 0 ? 'not-allowed' : 'pointer',
            fontFamily: 'Inter, sans-serif', opacity: activeSection === 0 ? 0.5 : 1,
          }}>← Previous Section</button>
          <button disabled={activeSection === OASIS_SCHEMA.length - 1}
            onClick={() => setActiveSection(s => s + 1)} style={{
            padding: '9px 18px', background: activeSection === OASIS_SCHEMA.length - 1 ? '#f3f4f6' : 'linear-gradient(180deg,#94335a,#7b1e3c)',
            color: activeSection === OASIS_SCHEMA.length - 1 ? '#374151' : '#fff',
            border: 'none', borderRadius: 8, cursor: activeSection === OASIS_SCHEMA.length - 1 ? 'not-allowed' : 'pointer',
            fontFamily: 'Inter, sans-serif', fontWeight: 600,
          }}>Next Section →</button>
        </div>
      </div>
    </div>
  );
}

function OasisItem({ item, value, onChange, disabled }) {
  const lbl = { display: 'block', fontSize: 13, fontWeight: 600, color: '#2b1b22', marginBottom: 8 };
  const sub = { fontSize: 11, color: '#8b7681', fontWeight: 400, marginLeft: 4 };

  return (
    <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
      <label style={lbl}>
        {item.id} <span style={sub}>{item.label}</span>
      </label>
      {item.type === 'text' && (
        <input style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7',
          borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
          value={value} disabled={disabled} onChange={e => onChange(e.target.value)} />
      )}
      {item.type === 'number' && (
        <input type="number" style={{ width: 120, padding: '8px 12px', border: '1px solid #ece3e7',
          borderRadius: 8, fontSize: 13 }}
          value={value} disabled={disabled} min={0}
          onChange={e => onChange(e.target.value)} />
      )}
      {item.type === 'radio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.options.map(([code, desc]) => (
            <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 8,
              cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, color: '#374151' }}>
              <input type="radio" name={item.id} value={code} checked={value === code}
                disabled={disabled} onChange={() => onChange(code)} />
              <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f3f4f6',
                padding: '1px 5px', borderRadius: 3, minWidth: 20, textAlign: 'center' }}>{code}</span>
              {desc}
            </label>
          ))}
        </div>
      )}
      {item.type === 'checkbox' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.options.map(([code, desc]) => {
            const selected = (value || '').split(',').filter(Boolean);
            const checked = selected.includes(code);
            return (
              <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 8,
                cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, color: '#374151' }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => {
                  const next = checked ? selected.filter(c => c !== code) : [...selected, code];
                  onChange(next.join(','));
                }} />
                {desc}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
